/*
  TYOHAAR smooth scrolling — the setup around Lenis.

  Lenis itself is assets/lenis.min.js, vendored and unmodified. Everything
  that decides WHETHER it should run, and everything that stops the rest of
  the theme tripping over it, is here.

  WHY LENIS AND NOT THE USUAL SMOOTH-SCROLL LIBRARY

  The older ones — Locomotive v4 and its imitators — hold the page still and
  slide a wrapper with transform. That looks the same and breaks everything:
  a transformed ancestor takes position:fixed out of the viewport and
  position:sticky stops sticking. This theme would lose the header (which is
  sticky), the search panel and its scrim (both fixed), the sticky product
  info and the sticky facets. We spent a session fixing exactly that class of
  bug when a scroll lock did the same thing.

  Lenis moves the real scroll position instead — window.scrollTo, every
  frame, eased. Sticky and fixed keep working because as far as the browser
  is concerned nothing unusual is happening. Anything that only READS the
  scroll position keeps working too, which is why animations.js and the
  header's own scroll-up logic needed no changes at all.

  THE FOUR GUARDS, AND WHY EACH ONE IS NOT OPTIONAL

  1. Off in the theme editor. The editor scrolls the preview to whichever
     section you select, by driving the scroll position directly. Lenis
     would be easing toward its own target at the same time and the two
     fight — the preview drifts, overshoots, or lands nowhere near.

  2. Off on touch. A phone's momentum comes from the OS and is tuned to the
     hardware. Replacing it with a JS easing curve is the single fastest way
     to make a site feel broken on a phone. Lenis agrees: syncTouch is off
     by default and it stays off. This is a pointer-fine enhancement.

  3. Off for prefers-reduced-motion. Non-negotiable, and consistent with the
     rest of the theme.

  4. Off if the merchant says so. It is a taste call — some people read
     eased scrolling as expensive, others as laggy — so it is a setting, and
     turning it off leaves the page on plain native scroll with no trace.

  Any guard failing means we return before Lenis is ever constructed. No
  instance, no rAF loop, no patched methods.
*/
(() => {
  const script = document.currentScript;

  const setting = (name, fallback) => {
    const raw = script?.dataset?.[name];
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  };

  /* -------------------------------------------------------------- guards */

  if (script?.dataset.enabled === 'false') return;
  if (window.Shopify?.designMode) return;
  if (!window.Lenis) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Coarse pointer covers phones and tablets, including the ones wide enough
  // to read as a laptop. A width check would not.
  if (window.matchMedia('(pointer: coarse)').matches) return;

  /* ---------------------------------------------------------------- lenis */

  /*
    LERP, NOT DURATION — AND THE DIFFERENCE IS THE WHOLE FEEL OF THE PAGE

    Lenis has two mutually exclusive modes, and its own advance() picks
    between them:

        if (this.duration && this.easing) { ...fixed-time easing... }
        else if (this.lerp)               { ...exponential decay...  }

    Passing a duration wins, and a duration is the wrong choice for a wheel.
    Two reasons, both visible on screen.

    FIRST: every wheel event restarts the animation. fromTo() sets
    `this.from = this.value` and `this.currentTime = 0`, so each notch begins
    a brand new curve from the current position at ZERO velocity. That is
    tolerable for one flick and awful for a trackpad, which keeps emitting
    inertia events for a second or so after the fingers lift. Each of those
    tail events resets the curve, and every reset is a velocity
    discontinuity. The page judders exactly where it should be gliding.

    SECOND: the tail of a duration curve is sub-pixel. With the ease-out
    everyone copies from the Lenis readme — 1.001 - 2^(-10t) — 97% of the
    distance is covered by the halfway point. The last third of the
    animation, 370ms of a 1.1s scroll, covers under 1% of it. Scroll 800px
    and that final third moves about 7px: a third of a pixel per frame.
    Browsers round the scroll position to whole device pixels, so most of
    those frames render no movement at all and then one jumps by a pixel.
    That is the "sticky" stop, and no amount of tuning the duration removes
    it, because it is the shape of the curve, not its length.

    Lerp has neither problem. It is exponential decay toward a moving
    target, so a new wheel event just moves the target — no restart, no
    velocity reset, and continuous scrolling stays continuous. And it ends
    honestly: advance() snaps and stops the moment the value rounds to the
    target, instead of grinding through a sub-pixel tail.

    It is also framerate-independent. Lenis calls advance(0.001 * ms), and
    the decay is 1 - e^(-60 * lerp * seconds), so a 120Hz screen and a 60Hz
    screen travel the same distance in the same time.

    Duration is still right for ANCHOR links further down — travelling to a
    named place is a journey with a destination, not a flick — so those pass
    a duration and easing per call.
  */
  const lenis = new window.Lenis({
    lerp: setting('lerp', 0.1),
    wheelMultiplier: setting('strength', 1),
    smoothWheel: true,
    syncTouch: false,
  });

  /* ------------------------------------------------------- the rAF loop */

  /*
    ONE CALL, NOTHING ELSE IN IT.

    There used to be two corrections here — the scroll position rounded to the
    device pixel grid, and a "settle" routine that took over the end of every
    glide. Both are gone. They were solving each other, and between them they
    were the extra movement at the end of a scroll.

    WHAT LENIS ACTUALLY DOES, FROM ITS OWN SOURCE

        this.value = lerp(this.value, this.to, 1 - Math.exp(-60 * lerp * dt));
        if (Math.round(this.value) === this.to) { this.value = this.to; stop(); }

    That second line is the part worth knowing: Lenis snaps and stops the
    moment the position rounds to the target — within half a pixel. Its decay
    does not trail off forever, so there was never a tail to rescue it from.

    WHAT THE CORRECTIONS DID TO EACH OTHER

    Rounding every frame to the device pixel grid meant that once the glide
    slowed below a pixel per frame, several frames in a row painted at the
    same offset and then one jumped. That is a stutter, and it was real — but
    it was caused by the rounding, not revealed by it. A browser scrolls to
    fractional offsets perfectly well; that is how its own smooth scrolling
    works.

    The settle routine was then written to hide that stutter, and it did it by
    taking over the last stretch at a CONSTANT one device pixel per frame. At
    lerp 0.09 each frame closes 8.6% of the gap, so per-frame movement drops
    below a pixel with 11.6px still to go. Those last 11.6px were then crawled
    at a flat 1px per frame — twelve frames, 200ms — after the deceleration
    had visually finished, and then stopped dead.

    Constant speed after a decay, ending abruptly. That is exactly what a
    "small extra movement, then a jerk" is. The library was never doing it.

    So the loop is one line again, and the glide ends the way the easing says
    it should: slowing continuously, then arriving.
  */
  const frame = (time) => {
    lenis.raf(time);
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // Exposed so anything else in the theme can scroll through it rather than
  // around it. Only defined when Lenis is actually running, so `window.lenis`
  // doubles as the honest answer to "is smooth scrolling on right now?".
  window.lenis = lenis;

  /* ----------------------------------------------------- header clearance */

  /*
    The header is sticky, so scrolling an element to the top of the viewport
    parks it underneath the bar. Everything below asks for this offset.
    Measured per call: the bar's height is a setting, its padding is fluid,
    and it changes between breakpoints.
  */
  const headerOffset = () => {
    const bar = document.querySelector('.tyoh__bar');
    return bar ? bar.getBoundingClientRect().height + 12 : 0;
  };

  /* ------------------------------------------------------------- anchors */

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link) return;

    // Modifier clicks and new tabs belong to the browser, not to us.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (link.target && link.target !== '_self') return;

    const hash = link.getAttribute('href');
    // "#" alone is the standard href for a button pretending to be a link.
    if (!hash || hash === '#') return;

    let target = null;
    try {
      target = document.querySelector(hash);
    } catch {
      // Not every "#..." is a valid selector — an id starting with a digit
      // throws rather than returning null.
      return;
    }
    if (!target) return;

    event.preventDefault();
    /*
      Per-call duration and easing, which overrides the instance's lerp for
      this one scroll. Going to a named destination should take a predictable
      amount of time whether it is 200px away or 8000 — decay alone makes a
      long jump feel like it will never arrive.
    */
    lenis.scrollTo(target, {
      offset: -headerOffset(),
      duration: setting('anchorDuration', 1.1),
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
  });

  /* --------------------------------------------- the rest of the theme */

  /*
    Dawn scrolls the page from a few places of its own. Left alone they call
    the browser's scroll while Lenis is easing toward somewhere else, and
    Lenis wins on the next frame: the jump either fights or is simply undone.

    WHY window.scrollTo IS NOT PATCHED HERE, THOUGH IT IS THE OBVIOUS MOVE

    Because Lenis drives the page through window.scrollTo itself — setScroll
    calls wrapper.scrollTo({ top, behavior: 'instant' }) on every single
    frame, and the wrapper is window. Wrapping that method means Lenis
    re-enters our code sixty times a second instead of moving the page.

    A behavior:'instant' check does dodge it, and it would work today. But it
    only works because of a detail of somebody else's internals, and the day
    a version bump changes that detail the failure is an infinite loop in
    production rather than a build error. Not worth it for what it buys:
    exactly ONE call site in this entire theme does a page-level animated
    window.scrollTo, and it is patched at source in media-gallery.js.

    (quick-order-list.js also calls it, with behavior:'instant' — a position
    being restored, not a journey. That one is right to stay native.)

    scrollIntoView is a different matter and IS routed below: Lenis never
    calls it, so there is no loop to walk into, and it is used from four
    places rather than one.
  */

  const nativeScrollIntoView = Element.prototype.scrollIntoView;

  /*
    scrollIntoView needs more care than a blanket redirect, because most of
    the calls in this theme are NOT about the page.

    The product thumbnails, the hero, the hampers rail and half a dozen other
    carousels are horizontal overflow containers, and they scroll themselves
    with this same method. Routing those through Lenis would heave the whole
    page around instead of nudging the strip — a far worse bug than the one
    being fixed.

    So: walk up looking for a scrollable ancestor. Find one, and this call was
    never the page's business — hand it straight back to the browser.
  */
  const scrollableAncestor = (element) => {
    let node = element.parentElement;

    while (node && node !== document.body && node !== document.documentElement) {
      const style = getComputedStyle(node);
      const overflow = `${style.overflowX}${style.overflowY}`;
      const scrolls = /(auto|scroll|overlay)/.test(overflow);
      const canScroll =
        node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1;

      if (scrolls && canScroll) return node;
      node = node.parentElement;
    }

    return null;
  };

  Element.prototype.scrollIntoView = function scrollIntoViewThroughLenis(...args) {
    if (scrollableAncestor(this)) return nativeScrollIntoView.apply(this, args);

    const options = typeof args[0] === 'object' ? args[0] : {};
    if (options.behavior === 'instant') return nativeScrollIntoView.apply(this, args);

    // block:'center' and 'end' are rare here and not worth guessing at; only
    // the default, top-of-viewport case needs the header out of the way.
    const offset = options.block && options.block !== 'start' ? 0 : -headerOffset();
    lenis.scrollTo(this, { offset });
  };
})();
